// /ask/$id — Individual AI Quick Answer page (one citable URL per question).
// Phase P-AI-CITATIONS-PER-QA: gives Google AI Overviews, Perplexity, ChatGPT,
// Bing Copilot, and Gemini a stable per-question canonical to cite directly.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Sparkles, ExternalLink, Phone, MessageCircle, ChevronRight, Calendar, ArrowLeft } from "lucide-react";
import { QUICK_ANSWERS, getQuickAnswer, type QuickAnswer } from "@/data/quick-answers";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, qaPageSchema, organizationSchema, BUILD_DATE } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { getReviewerForCategory } from "@/data/qa-reviewers";

const SITE = "https://nyclanguageinstitute.com";
const SOCIAL_IMG = "https://nyclanguageinstitute.com/social/nyc-legal-og.webp";

export const Route = createFileRoute("/ask/$id")({
  loader: ({ params }) => {
    const qa = getQuickAnswer(params.id);
    if (!qa) throw notFound();
    return { qa };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const q = loaderData.qa;
    const url = `${SITE}/ask/${q.id}`;
    const title = `${q.question} — คำตอบจากผู้เชี่ยวชาญ | NYC Legal & Notary`;
    const desc = q.shortAnswer.slice(0, 158);
    return {
      meta: [
        { title: ctaTitle(title, "/ask/$id", "th") },
        { name: "description", content: ctaDesc(desc, "/ask/$id", "th")},
        { name: "keywords", content: `${q.category}, ${q.question}, NYC Legal, quick answer, AI search` },
        { property: "og:title", content: ctaTitle(title, "/ask/$id", "th")},
        { property: "og:description", content: ctaDesc(desc, "/ask/$id", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/ask/$id", "th")},
        { name: "twitter:description", content: desc },
        { property: "og:image", content: SOCIAL_IMG },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: title },
        { name: "twitter:image", content: SOCIAL_IMG },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/ask/${q.id}`, `/en/ask/${q.id}`),
      ],
      scripts: [
        consolidateGraph([
          organizationSchema(),
          speakableSchema([".aeo-speakable", ".quick-answer", "h1", "h2"]),
          qaPageSchema({ url, question: q.question, answer: q.fullAnswer, upvoteCount: 5 }),
          (() => {
            const reviewer = getReviewerForCategory(q.categoryEn);
            const reviewerUrl = `${SITE}/about/team/${reviewer.slug}`;
            return {
              type: "application/ld+json" as const,
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "@id": `${url}#article`,
                headline: q.question,
                description: desc,
                image: [SOCIAL_IMG],
                articleBody: q.fullAnswer,
                inLanguage: "th",
                // Phase 4 E-E-A-T: real Person as author + reviewedBy
                author: {
                  "@type": "Person",
                  "@id": `${reviewerUrl}#person`,
                  name: reviewer.nameEn,
                  jobTitle: reviewer.roleEn,
                  url: reviewerUrl,
                },
                reviewedBy: {
                  "@type": "Person",
                  "@id": `${reviewerUrl}#person`,
                  name: reviewer.nameEn,
                  url: reviewerUrl,
                },
                publisher: {
                  "@type": "Organization",
                  name: "NYC Legal & Notary Service",
                  url: SITE,
                  logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
                },
                datePublished: q.lastReviewed,
                dateModified: BUILD_DATE,
                mainEntityOfPage: url,
                articleSection: q.category,
                about: { "@type": "Thing", name: q.category },
                // Citation graph: link to all authoritative sources
                citation: (q.sources ?? []).map((s) => ({
                  "@type": "CreativeWork",
                  name: s.name,
                  url: s.url,
                })),
              }),
            };
          })(),
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "AI Quick Answers", item: `${SITE}/ask` },
                { "@type": "ListItem", position: 3, name: q.question, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบคำถามนี้</h1>
      <Link to="/ask" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">
        กลับสู่ AI Quick Answers
      </Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">เกิดข้อผิดพลาด</h1>
      <Link to="/ask" className="mt-6 inline-block text-primary">ย้อนกลับ</Link>
    </main>
  ),
  component: AskDetail,
});

function AskDetail() {
  const { qa } = Route.useLoaderData();
  const related: QuickAnswer[] = (qa.related ?? [])
    .map((id: string) => QUICK_ANSWERS.find((x) => x.id === id))
    .filter((x: QuickAnswer | undefined): x is QuickAnswer => x !== undefined);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <nav className="mx-auto max-w-3xl px-4 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">หน้าแรก</Link>
        <ChevronRight className="mx-1 inline h-3 w-3" />
        <Link to="/ask" className="hover:underline">AI Quick Answers</Link>
        <ChevronRight className="mx-1 inline h-3 w-3" />
        <span className="text-foreground">{qa.category}</span>
      </nav>

      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-8 pb-12">
        <div className="mx-auto max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary">
            <Sparkles className="h-3 w-3" /> {qa.category} · AI-Optimized Answer
          </div>
          <h1 className="text-2xl font-extrabold leading-snug sm:text-4xl aeo-speakable">{qa.question}</h1>

          <div
            className="quick-answer aeo-speakable mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5"
            itemScope
            itemType="https://schema.org/Question"
          >
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg" itemProp="name">{qa.question}</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-base font-semibold text-foreground" itemProp="text">
                {qa.shortAnswer}
              </p>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-border bg-card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">รายละเอียดเชิงลึก</h2>
            <p className="mt-3 text-base leading-relaxed text-foreground">{qa.fullAnswer}</p>
          </div>

          <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            ตรวจสอบล่าสุด {qa.lastReviewed} · อัปเดต {BUILD_DATE}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 083-249-4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
          </div>
        </div>
      </section>

      {qa.sources && qa.sources.length > 0 && (
        <section className="bg-muted/30 px-4 py-10">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-lg font-bold">แหล่งอ้างอิงทางการ · References</h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {qa.sources.map((s: { name: string; url: string }) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm hover:border-primary"
                  >
                    <ExternalLink className="h-4 w-4 text-primary" />
                    <span className="font-medium">{s.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="px-4 py-10">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-lg font-bold">คำถามที่เกี่ยวข้อง</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {related.map((r: QuickAnswer) => (
                <Link
                  key={r.id}
                  to="/ask/$id"
                  params={{ id: r.id }}
                  className="group rounded-xl border border-border bg-card p-4 hover:border-primary"
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider text-primary">{r.category}</div>
                  <div className="mt-1 text-sm font-bold leading-snug">{r.question}</div>
                  <div className="mt-2 text-xs text-muted-foreground">{r.shortAnswer}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-4 pb-16">
        <div className="mx-auto max-w-3xl">
          <Link
            to="/ask"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> กลับสู่ AI Quick Answers ทั้งหมด
          </Link>
        </div>
      </section>
    </main>
  );
}
